import type { ApiCreateMessageRequest, MessagesResponse, Chat } from './api';
import type { AppUpdate } from '@/types';
import stylegenJulyUpdatesPng from '../assets/stylegen-julyupdates.png';

/**
 * MockApiService - Simulates backend API for development and testing
 * 
 * This service provides mock data for the API endpoints when VITE_USE_MOCK_API=true
 * For error testing, use the ErrorTestingService by setting VITE_USE_ERROR_TESTING=true
 */

const MOCK_DISMISSED_KEY = 'mock_dismissed_updates';
const MOCK_SESSION_KEY = 'mock_session_id';

// Clear dismissed updates whenever a new browser session starts so the modal
// always appears on page load during mock development.
function clearDismissedForNewSession(): void {
  try {
    const sessionId = sessionStorage.getItem(MOCK_SESSION_KEY);
    if (!sessionId) {
      localStorage.removeItem(MOCK_DISMISSED_KEY);
      sessionStorage.setItem(MOCK_SESSION_KEY, '1');
    }
  } catch { /* storage unavailable */ }
}
clearDismissedForNewSession();

function loadDismissed(): Set<number> {
  try {
    const raw = localStorage.getItem(MOCK_DISMISSED_KEY);
    return raw ? new Set<number>(JSON.parse(raw) as number[]) : new Set<number>();
  } catch {
    return new Set<number>();
  }
}

function saveDismissed(ids: Set<number>): void {
  try {
    localStorage.setItem(MOCK_DISMISSED_KEY, JSON.stringify([...ids]));
  } catch { /* storage unavailable */ }
}

export class MockApiService {
  private static readonly chats: Chat[] = MockApiService.createInitialChats();
  private static readonly dismissedUpdateIds: Set<number> = loadDismissed();

  private static createInitialChats(): Chat[] {
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    return [
      {
        id: 1,
        name: 'General Discussion',
        isPinned: false,
        createdAt: new Date(now - oneDay).toISOString(),
        updatedAt: new Date(now - (5 * 60 * 60 * 1000)).toISOString(),
        deleted: false
      },
      {
        id: 2,
        name: 'Fall Collection',
        isPinned: false,
        createdAt: new Date(now - (2 * oneDay)).toISOString(),
        updatedAt: new Date(now - (12 * 60 * 60 * 1000)).toISOString(),
        deleted: false
      },
      {
        id: 3,
        name: 'Jeans Project',
        isPinned: false,
        createdAt: new Date(now - (3 * oneDay)).toISOString(),
        updatedAt: new Date(now - (1 * oneDay + 8 * 60 * 60 * 1000)).toISOString(),
        deleted: false
      },
      {
        id: 4,
        name: 'Summer Campaign',
        isPinned: false,
        createdAt: new Date(now - (4 * oneDay)).toISOString(),
        updatedAt: new Date(now - (3 * oneDay + 14 * 60 * 60 * 1000)).toISOString(),
        deleted: false
      }
    ];
  }

  /**
   * Mock implementation for creating a message
   */
  static async createMessage(messageData: ApiCreateMessageRequest): Promise<MessagesResponse> {
    console.log('📝 Mock API: Creating message');
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Generate or use existing chatId
    const chatId = messageData.ChatId || Math.floor(Math.random() * 1000000);
    
    // Log the received metadata for debugging
    console.log('📝 Mock API: Received metadata:', messageData.Metadata);
    
    // Return mock response structure
    return [
      {
        id: messageData.Id, // Use the ID from the request
        chatId: chatId,
        text: messageData.Message,
        status: 'sent',
        created_at: new Date().toISOString(),
        favourite: false,
        type: 'user',
        parent_id: null,
        deleted: false,
        metadata: messageData.Metadata || {},
        attachments: []
      },
      {
        id: Math.random().toString(36).slice(2), // Generate new ID for AI response
        chatId: chatId,
        text: `Mock AI response to: ${messageData.Message} (Request type: ${messageData.Metadata?.request_type || 'unknown'}, Aspect ratio: ${messageData.Metadata?.aspect_ratio || 'unknown'})`,
        status: 'completed',
        created_at: new Date().toISOString(),
        favourite: false,
        type: 'ai',
        parent_id: null,
        deleted: false,
        metadata: messageData.Metadata || {},
        attachments: []
      }
    ];
  }

  /**
   * Mock implementation for getting chats
   */
  static async getChats(): Promise<Chat[]> {
    console.log('📋 Mock API: Getting chats');
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return MockApiService.chats
      .filter(chat => !chat.deleted)
      .map(chat => ({ ...chat }));
  }

  static async renameChat(chatId: number, name: string): Promise<void> {
    console.log(`✏️ Mock API: Renaming chat ${chatId} to "${name}"`);

    await new Promise(resolve => setTimeout(resolve, 400));

    const chat = MockApiService.chats.find(existingChat => existingChat.id === chatId);
    if (chat) {
      chat.name = name;
      chat.updatedAt = new Date().toISOString();
    }
  }

  static async pinChat(chatId: number, isPinned: boolean): Promise<Chat> {
    console.log(`📌 Mock API: Setting chat ${chatId} pin state to ${isPinned}`);

    await new Promise(resolve => setTimeout(resolve, 350));

    const chat = MockApiService.chats.find(existingChat => existingChat.id === chatId);
    if (!chat) {
      throw new Error(`Chat ${chatId} not found`);
    }

    chat.isPinned = isPinned;
    chat.updatedAt = new Date().toISOString();
    return { ...chat };
  }

   /**
   *Mock implementation for deleting chat
   */

  static async deleteChat(chatId: number): Promise<void> {
  console.log(`🗑️ Mock API: Deleting chat ${chatId}`);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  // Since this is a mock, we don't actually remove anything,
  // but in a more advanced mock you could keep state and filter it out.
  console.log(`🗑️ Mock API: Chat ${chatId} deleted (simulated)`);
}

  /**
   * Mock implementation for getting messages for a specific chat
   */
  static async getMessages(chatId: number): Promise<import('./api').Message[]> {
    console.log(`Mock API: Getting messages for chat ${chatId}`);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return mock messages data based on chat ID
    // Use current time minus some interval to make messages appear recent
    const now = Date.now();
    const baseTime = now - (2 * 60 * 60 * 1000); // 2 hours ago
    
    return [
      {
        id: `msg-${chatId}-1`,
        chatId: chatId,
        text: `Hello! This is the first message in chat ${chatId}.`,
        status: 'completed',
        created_at: new Date(baseTime).toISOString(),
        favourite: false,
        type: 'user',
        parent_id: null,
        deleted: false,
        metadata: {},
        attachments: []
      },
      {
        id: `msg-${chatId}-2`,
        chatId: chatId,
        text: `This is a mock AI response for chat ${chatId}. How can I help you today?`,
        status: 'completed',
        created_at: new Date(baseTime + 30000).toISOString(), // 30 seconds later
        favourite: false,
        type: 'ai',
        parent_id: null,
        deleted: false,
        metadata: {},
        attachments: []
      },
      {
        id: `msg-${chatId}-3`,
        chatId: chatId,
        text: `Here's another message to show the conversation flow in chat ${chatId}.`,
        status: 'completed',
        created_at: new Date(baseTime + 60000).toISOString(), // 1 minute later
        favourite: false,
        type: 'user',
        parent_id: null,
        deleted: false,
        metadata: {},
        attachments: []
      }
    ];
  }

  /**
   * Mock implementation for the active update check
   */
  static async getActiveUpdate(): Promise<AppUpdate | null> {
    console.log('🔔 Mock API: Getting active update');
    await new Promise(resolve => setTimeout(resolve, 300));
    const update: AppUpdate = {
      id: 1,
      title: 'Update!',
      body: "We have a Stylegen Update!\n\nToday we have released more updates to Stylegen based on your feedback!\n\nBelow you can see the highlights of improvements.\n\n• Multiple Colorway requests in one prompt\n• Print Scale improvements\n• Dyslexia font added (Can be turned on or off at the bottom of the left side pop out menu.)  \n\nClick here to see a demo video of the above features:",
      imageUrl: stylegenJulyUpdatesPng,
      demoUrl: 'https://example.com/demo',
      createdAt: new Date().toISOString(),
    };
    if (MockApiService.dismissedUpdateIds.has(update.id)) {
      console.log(`🔔 Mock API: Update ${update.id} already dismissed, returning null`);
      return null;
    }
    return update;
  }

  static async dismissUpdate(id: number): Promise<void> {
    console.log(`🔔 Mock API: Dismissing update ${id}`);
    MockApiService.dismissedUpdateIds.add(id);
    saveDismissed(MockApiService.dismissedUpdateIds);
  }
}
